import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  getCurrentUniverse, 
  setCurrentUniverse, 
  UNIVERSES, 
  ASSETS,
  SUPER_HERO_CHARACTERS,
  GLOBE_TROTTER_CHARACTERS,
  getRandomQuote
} from '../config/schools';
import { FaMask, FaGlobe, FaCheck, FaUsers } from 'react-icons/fa';

function UniverseSelector({ onSelect, showAsModal = false }) {
  const [selectedUniverse, setSelectedUniverse] = useState(getCurrentUniverse());
  const [isVisible, setIsVisible] = useState(showAsModal);

  const handleSelect = (universe) => {
    setSelectedUniverse(universe);
    setCurrentUniverse(universe);
    if (onSelect) {
      onSelect(universe);
    }
    if (showAsModal) {
      setIsVisible(false);
    }
  };

  // Liste des personnages pour chaque univers
  const superHeroTeam = [
    { name: 'Super Reporter', role: 'Guide principal' },
    { name: 'Super Red Chief', role: 'Grammaire' },
    { name: 'Super SEO Girl', role: 'Référencement' },
    { name: 'Super Interviewer', role: 'Interview' },
    { name: 'Super Fact-Checker', role: 'Vérification' },
    { name: 'Super Pitcher', role: 'Pitch' }
  ];

  const globeTrotterTeam = [
    { name: 'Reporter Petit XXème', role: 'Guide principal' },
    { name: 'Le Capitaine (Archibald)', role: 'Grammaire' },
    { name: 'La Diva (Bianca)', role: 'Interview' },
    { name: 'Séraphin', role: 'SEO' },
    { name: 'Les Policiers', role: 'Vérification' },
    { name: 'Nestor', role: 'Pitch' },
    { name: 'Le Professeur (Triphon)', role: 'Technique' }
  ];

  const universes = [
    {
      id: UNIVERSES.SUPER_HERO,
      name: 'Super-Héros',
      description: 'Des super-héros du journalisme avec leurs pouvoirs uniques !',
      icon: <FaMask className="text-3xl" />,
      image: ASSETS.SUPER_REPORTER,
      color: '#E63946',
      character: SUPER_HERO_CHARACTERS.main,
      team: superHeroTeam
    },
    {
      id: UNIVERSES.GLOBE_TROTTER,
      name: 'Globe-Trotter',
      description: 'L\'aventure avec le Reporter Petit XXème et ses amis !',
      icon: <FaGlobe className="text-3xl" />,
      image: ASSETS.PETIT_XXEME,
      color: '#1B4965',
      character: GLOBE_TROTTER_CHARACTERS.petitXXeme,
      team: globeTrotterTeam
    }
  ];

  const content = (
    <div className="p-6">
      <h2 className="comic-title text-2xl md:text-3xl text-center mb-2" style={{ color: '#1B4965' }}>
        Choisis ton univers
      </h2>
      <p className="text-center text-gray-600 mb-6 text-sm">
        Chaque univers a ses propres coachs qui t'accompagneront dans tes missions !
      </p>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {universes.map((universe) => (
          <motion.button
            key={universe.id}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => handleSelect(universe.id)}
            className={`comic-panel p-5 text-left relative overflow-hidden transition-all ${
              selectedUniverse === universe.id 
                ? 'border-4 shadow-lg' 
                : 'border-2 hover:border-gray-400'
            }`}
            style={{ 
              borderColor: selectedUniverse === universe.id ? universe.color : undefined 
            }}
            data-testid={`universe-${universe.id}`}
          >
            {/* Selection indicator */}
            {selectedUniverse === universe.id && (
              <div 
                className="absolute top-2 right-2 w-8 h-8 rounded-full flex items-center justify-center text-white"
                style={{ background: universe.color }}
              >
                <FaCheck />
              </div>
            )}
            
            {/* Header with icon and name */}
            <div className="flex items-center mb-4">
              <div 
                className="w-16 h-16 rounded-full flex items-center justify-center mr-4 overflow-hidden"
                style={{ background: `${universe.color}20` }}
              >
                <img 
                  src={universe.image} 
                  alt={universe.name}
                  className="w-14 h-14 object-contain"
                />
              </div>
              <div>
                <div className="flex items-center space-x-2">
                  <span style={{ color: universe.color }}>{universe.icon}</span>
                  <h3 className="comic-title text-xl" style={{ color: universe.color }}>
                    {universe.name}
                  </h3>
                </div>
                <p className="text-xs text-gray-500">{universe.description}</p>
              </div>
            </div>
            
            {/* Team members */}
            <div className="mb-3">
              <div className="flex items-center text-xs text-gray-500 mb-2">
                <FaUsers className="mr-1" />
                <span>Équipe de coachs :</span>
              </div>
              <div className="flex flex-wrap gap-1">
                {universe.team.slice(0, 5).map((member, idx) => (
                  <span 
                    key={idx}
                    className="text-xs px-2 py-1 rounded-full"
                    style={{ 
                      background: `${universe.color}15`,
                      color: universe.color
                    }}
                  >
                    {member.name.split(' (')[0]}
                  </span>
                ))}
                {universe.team.length > 5 && (
                  <span className="text-xs px-2 py-1 text-gray-400">
                    +{universe.team.length - 5}
                  </span>
                )}
              </div>
            </div>
            
            {/* Character quote */}
            <div className="speech-bubble text-xs p-3">
              <p className="italic">"{getRandomQuote(universe.character)}"</p>
              <p className="text-gray-500 mt-1">— {universe.character.name}</p>
            </div>
          </motion.button>
        ))}
      </div>
    </div>
  );

  if (showAsModal) {
    return (
      <AnimatePresence>
        {isVisible && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          >
            <motion.div
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto"
              style={{ background: '#F8F4E3' }}
            >
              {content}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    );
  }

  return content;
}

// Bouton compact pour changer d'univers
export function UniverseToggle({ className = '' }) {
  const [showSelector, setShowSelector] = useState(false);
  const [currentUniverse, setCurrentUniverseState] = useState(getCurrentUniverse());

  const handleSelect = (universe) => {
    setCurrentUniverseState(universe);
    setShowSelector(false);
    // Reload to apply new universe
    window.location.reload();
  };

  const isGlobeTrotter = currentUniverse === UNIVERSES.GLOBE_TROTTER;

  return (
    <>
      <button
        onClick={() => setShowSelector(true)}
        className={`comic-button flex items-center space-x-2 ${className}`}
        data-testid="universe-toggle"
        title="Changer d'univers"
      >
        {isGlobeTrotter ? (
          <>
            <FaGlobe />
            <span className="hidden md:inline">Globe-Trotter</span>
          </>
        ) : (
          <>
            <FaMask />
            <span className="hidden md:inline">Super-Héros</span>
          </>
        )}
      </button>

      <AnimatePresence>
        {showSelector && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowSelector(false)}
          >
            <motion.div
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              className="bg-white rounded-lg max-w-2xl w-full"
              style={{ background: '#F8F4E3' }}
              onClick={(e) => e.stopPropagation()}
            >
              <UniverseSelector onSelect={handleSelect} />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

export default UniverseSelector;
